import React from 'react';
import styled from '@emotion/styled';
import { Autocomplete, Chip, TextField } from '@mui/material';
import { validEmail } from '@/_utils/utils';
import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import SortableItem from './SortableItem';
import { useToast } from './Toast';

type EmailsFieldProps = {
  name: string;
  emails?: string[];
  label?: string;
  placeholder?: string;
  onChange: (emails: string[]) => void;
  disabled?: boolean;
  hasError?: boolean;
};

const EmailsField = ({
  name,
  emails = [],
  label = 'Emails:',
  placeholder = 'Type email and press enter',
  onChange,
  disabled,
  hasError
}: EmailsFieldProps) => {
  const { error } = useToast();

  const validateAndAddEmail = (newEmail: string) => {
    if (!validEmail(newEmail)) {
      error(`${newEmail} is not a valid email address.`);
    } else {
      if (emails.map((email) => email.toLowerCase()).indexOf(newEmail.toLowerCase()) === -1) {
        emails.push(newEmail);
        onChange(emails);
      } else {
        error(`${newEmail} is already added.`);
      }
    }
  };

  const handleOnChange = (e: any, val: string[], reason: string) => {
    if (reason === 'createOption' || reason === 'blur') {
      if (!e.target.value.length) return;
      const newEmails = e.target.value;
      let emails;
      if (newEmails.includes(',')) {
        emails = newEmails.split(',');
      } else {
        emails = [newEmails];
      }
      emails.map((newEmail: string) => validateAndAddEmail(newEmail.trim()));
    } else {
      return onChange(val);
    }
  };

  const { setNodeRef } = useDroppable({
    id: name,
  });

  return (
    <SortableContext id={name} items={emails} strategy={verticalListSortingStrategy}>
      <DroppableDiv ref={setNodeRef}>
        <Autocomplete
          multiple
          disabled={disabled}
          id="tags-filled"
          options={[]}
          freeSolo
          value={emails}
          onChange={handleOnChange}
          autoSelect
          clearOnBlur
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <SortableItem key={option} id={`${name}-${option}`}>
                <EmailChip style={{ cursor: 'grab' }} variant="filled" label={option} {...getTagProps({ index })} />
              </SortableItem>
            ))
          }
          renderInput={(params) => (
            <TextField error={hasError} {...params} label={label} placeholder={placeholder} variant="filled" disabled={disabled} />
          )}
        />
      </DroppableDiv>
    </SortableContext>
  );
};

export default EmailsField;

const DroppableDiv = styled.div`
&& {
  .MuiFilledInput-root {
    display: flex;
    flex-direction: column;
    align-items: start;

    input {
      width: 100%;
    }
  }
}
`;

const EmailChip = styled(Chip)`
&& {
  .MuiChip-label {
    display: flex;
    align-items: center;
  }
}
`;